import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { InvestorCommunicationType } from "../../../api/types/investorCommunicationsTypes";
import NewInvestorCommunicationTypeCard from "./NewInvestorCommunicationTypeCard";

interface TypeOption {
  type: InvestorCommunicationType;
  description: string;
}

const typeOptions: TypeOption[] = [
  {
    type: "Article",
    description:
      "Written update with a hero image and rich text body. Published to the News section of the Investor Portal.",
  },
  {
    type: "Video",
    description: "Uploaded video with a thumbnail. Published to the News section of the Investor Portal.",
  },
];

const createInvestorCommunication = withErrorHandling(adminApi.createInvestorCommunication);

interface Props {
  onClose: () => void;
  onCreated: (communicationId: string) => void;
}

const NewInvestorCommunicationDialog = ({ onClose, onCreated }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [selectedType, setSelectedType] = useState<InvestorCommunicationType>("Article");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    setIsCreating(true);
    const [communication, error] = await createInvestorCommunication(selectedType);
    setIsCreating(false);

    if (error) {
      logError(error, "[NewInvestorCommunicationDialog] createInvestorCommunication");
      sendNotificationError(error.message);
      return;
    }

    onCreated(communication.id);
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle>New communication</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Stack spacing={2}>
          <Typography color="textSecondary">
            Choose what you want to create. You can set the audience and delivery in the next steps.
          </Typography>

          {typeOptions.map(({ type, description }) => (
            <NewInvestorCommunicationTypeCard
              key={type}
              type={type}
              description={description}
              disabled={isCreating}
              selected={type === selectedType}
              onClick={() => setSelectedType(type)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={isCreating}>
        <Button variant="contained" loading={isCreating} onClick={handleCreate}>
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default NewInvestorCommunicationDialog;
